import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Plus, Trash2, Loader2, Building2, User, Edit } from "lucide-react";
import { format } from "date-fns";
import type { Account, AccountPeople, TrusteeCompany, Contact } from "@shared/schema";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { StructureDatePicker } from "@/components/ui/structure-date-picker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ContactSelect } from "@/components/form-fields/contact-select";
import { ContactFormModal } from "@/components/contact-form-modal";
import { AccountFormModal } from "@/components/account-form-modal";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { buildNavigationUrl } from "@/lib/breadcrumbs";

interface IndividualTrusteeWithContact extends AccountPeople {
  contact?: Contact;
}

interface CorporateTrusteeWithAccount extends TrusteeCompany {
  trusteeAccount?: Account;
}

interface TrusteesPanelProps {
  account: Account;
}

export function TrusteesPanel({ account }: TrusteesPanelProps) {
  const [, navigate] = useLocation();
  const [isAddIndividualDialogOpen, setIsAddIndividualDialogOpen] = useState(false);
  const [isAddCorporateDialogOpen, setIsAddCorporateDialogOpen] = useState(false);
  const [isEditIndividualDialogOpen, setIsEditIndividualDialogOpen] = useState(false);
  const [isEditCorporateDialogOpen, setIsEditCorporateDialogOpen] = useState(false);
  const [showCreateContactDialog, setShowCreateContactDialog] = useState(false);
  const [showCreateCompanyDialog, setShowCreateCompanyDialog] = useState(false);
  const [selectedContactId, setSelectedContactId] = useState<string | null>(null);
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const [appointmentDate, setAppointmentDate] = useState<Date | undefined>();
  const [editingIndividualTrustee, setEditingIndividualTrustee] = useState<IndividualTrusteeWithContact | null>(null);
  const [editingCorporateTrustee, setEditingCorporateTrustee] = useState<CorporateTrusteeWithAccount | null>(null);
  const [editCeasedDate, setEditCeasedDate] = useState<Date | undefined>();
  const [editAppointmentDate, setEditAppointmentDate] = useState<Date | undefined>();
  const [deleteIndividualId, setDeleteIndividualId] = useState<string | null>(null);
  const [deleteCorporateId, setDeleteCorporateId] = useState<string | null>(null);
  const { toast } = useToast();

  const { data: individualTrustees = [], isLoading: isLoadingIndividual } = useQuery<IndividualTrusteeWithContact[]>({
    queryKey: ["/api/crm/accounts", account.id, "trustees", "individual"],
    enabled: account.entityType === "trust",
  });

  const { data: corporateTrustees = [], isLoading: isLoadingCorporate } = useQuery<CorporateTrusteeWithAccount[]>({
    queryKey: ["/api/crm/accounts", account.id, "trustees", "corporate"],
    enabled: account.entityType === "trust",
  });

  const { data: contacts = [] } = useQuery<Contact[]>({
    queryKey: ["/api/crm/contacts", { clientGroupId: account.clientGroupId }],
    enabled: isAddIndividualDialogOpen,
  });

  const { data: companyAccounts = [] } = useQuery<Account[]>({
    queryKey: ["/api/crm/accounts", { clientGroupId: account.clientGroupId, entityType: "company" }],
    enabled: isAddCorporateDialogOpen,
  });

  const addIndividualMutation = useMutation({
    mutationFn: async (data: { contactId: string; appointmentDate?: string }) => {
      await apiRequest("POST", `/api/crm/accounts/${account.id}/people`, {
        contactId: data.contactId,
        role: "trustee",
        appointmentDate: data.appointmentDate,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/crm/accounts", account.id, "trustees", "individual"] });
      toast({ title: "Trustee added", description: "The individual trustee has been added successfully." });
      handleCloseIndividualDialog();
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message || "Failed to add trustee", variant: "destructive" });
    },
  });

  const addCorporateMutation = useMutation({
    mutationFn: async (data: { trusteeAccountId: string; appointmentDate?: string }) => {
      await apiRequest("POST", `/api/crm/accounts/${account.id}/trustees/corporate`, {
        trusteeAccountId: data.trusteeAccountId,
        appointmentDate: data.appointmentDate,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/crm/accounts", account.id, "trustees", "corporate"] });
      toast({ title: "Trustee added", description: "The corporate trustee has been added successfully." });
      handleCloseCorporateDialog();
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message || "Failed to add corporate trustee", variant: "destructive" });
    },
  });

  const deleteIndividualMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/crm/accounts/${account.id}/people/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/crm/accounts", account.id, "trustees", "individual"] });
      toast({ title: "Trustee removed", description: "The individual trustee has been removed." });
      setDeleteIndividualId(null);
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message || "Failed to remove trustee", variant: "destructive" });
    },
  });

  const deleteCorporateMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/crm/accounts/${account.id}/trustees/corporate/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/crm/accounts", account.id, "trustees", "corporate"] });
      toast({ title: "Trustee removed", description: "The corporate trustee has been removed." });
      setDeleteCorporateId(null);
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message || "Failed to remove corporate trustee", variant: "destructive" });
    },
  });

  const editIndividualMutation = useMutation({
    mutationFn: async (data: { id: string; ceasedDate: string | null; appointmentDate: string | null }) => {
      await apiRequest("PATCH", `/api/crm/accounts/${account.id}/people/${data.id}`, {
        ceasedDate: data.ceasedDate,
        appointmentDate: data.appointmentDate,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/crm/accounts", account.id, "trustees", "individual"] });
      toast({ title: "Trustee updated", description: "The trustee has been updated successfully." });
      handleCloseEditIndividualDialog();
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message || "Failed to update trustee", variant: "destructive" });
    },
  });

  const editCorporateMutation = useMutation({
    mutationFn: async (data: { id: string; ceasedDate: string | null; appointmentDate: string | null }) => {
      await apiRequest("PATCH", `/api/crm/accounts/${account.id}/trustees/corporate/${data.id}`, {
        ceasedDate: data.ceasedDate,
        appointmentDate: data.appointmentDate,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/crm/accounts", account.id, "trustees", "corporate"] });
      toast({ title: "Trustee updated", description: "The trustee has been updated successfully." });
      handleCloseEditCorporateDialog();
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message || "Failed to update corporate trustee", variant: "destructive" });
    },
  });

  const handleCloseIndividualDialog = () => {
    setIsAddIndividualDialogOpen(false);
    setSelectedContactId(null);
    setAppointmentDate(undefined);
  };

  const handleCloseCorporateDialog = () => {
    setIsAddCorporateDialogOpen(false);
    setSelectedAccountId(null);
    setAppointmentDate(undefined);
  };

  const handleAddIndividual = () => {
    if (!selectedContactId) return;
    addIndividualMutation.mutate({
      contactId: selectedContactId,
      appointmentDate: appointmentDate ? format(appointmentDate, "yyyy-MM-dd") : undefined,
    });
  };

  const handleAddCorporate = () => {
    if (!selectedAccountId) return;
    addCorporateMutation.mutate({
      trusteeAccountId: selectedAccountId,
      appointmentDate: appointmentDate ? format(appointmentDate, "yyyy-MM-dd") : undefined,
    });
  };

  const handleOpenEditIndividual = (trustee: IndividualTrusteeWithContact) => {
    setEditingIndividualTrustee(trustee);
    setEditCeasedDate(trustee.ceasedDate ? new Date(trustee.ceasedDate) : undefined);
    setEditAppointmentDate(trustee.appointmentDate ? new Date(trustee.appointmentDate) : undefined);
    setIsEditIndividualDialogOpen(true);
  };

  const handleCloseEditIndividualDialog = () => {
    setIsEditIndividualDialogOpen(false);
    setEditingIndividualTrustee(null);
    setEditCeasedDate(undefined);
    setEditAppointmentDate(undefined);
  };

  const handleEditIndividual = () => {
    if (!editingIndividualTrustee) return;
    editIndividualMutation.mutate({
      id: editingIndividualTrustee.id,
      ceasedDate: editCeasedDate ? format(editCeasedDate, "yyyy-MM-dd") : null,
      appointmentDate: editAppointmentDate ? format(editAppointmentDate, "yyyy-MM-dd") : null,
    });
  };

  const handleOpenEditCorporate = (trustee: CorporateTrusteeWithAccount) => {
    setEditingCorporateTrustee(trustee);
    setEditCeasedDate(trustee.ceasedDate ? new Date(trustee.ceasedDate) : undefined);
    setEditAppointmentDate(trustee.appointmentDate ? new Date(trustee.appointmentDate) : undefined);
    setIsEditCorporateDialogOpen(true);
  };

  const handleCloseEditCorporateDialog = () => {
    setIsEditCorporateDialogOpen(false);
    setEditingCorporateTrustee(null);
    setEditCeasedDate(undefined);
    setEditAppointmentDate(undefined);
  };

  const handleEditCorporate = () => {
    if (!editingCorporateTrustee) return;
    editCorporateMutation.mutate({
      id: editingCorporateTrustee.id,
      ceasedDate: editCeasedDate ? format(editCeasedDate, "yyyy-MM-dd") : null,
      appointmentDate: editAppointmentDate ? format(editAppointmentDate, "yyyy-MM-dd") : null,
    });
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-AU", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getContactName = (trustee: IndividualTrusteeWithContact) => {
    if (trustee.contact) {
      return `${trustee.contact.firstName || ""} ${trustee.contact.lastName || ""}`.trim() || "Unknown";
    }
    const contact = contacts.find(c => c.id === trustee.contactId);
    if (contact) {
      return `${contact.firstName || ""} ${contact.lastName || ""}`.trim() || "Unknown";
    }
    return "Unknown";
  };

  const getCompanyName = (trustee: CorporateTrusteeWithAccount) => {
    if (trustee.trusteeAccount) {
      return trustee.trusteeAccount.legalName;
    }
    const acc = companyAccounts.find(a => a.id === trustee.trusteeAccountId);
    return acc?.legalName || "Unknown Company";
  };

  const getCompanyAcn = (trustee: CorporateTrusteeWithAccount) => {
    if (trustee.trusteeAccount) {
      return trustee.trusteeAccount.acn;
    }
    const acc = companyAccounts.find(a => a.id === trustee.trusteeAccountId);
    return acc?.acn || null;
  };

  if (account.entityType !== "trust") {
    return null;
  }

  const isLoading = isLoadingIndividual || isLoadingCorporate;

  // AU trust rule: individual + corporate trustees cannot coexist
  const hasActiveIndividualTrustees = individualTrustees.some(
    (t) => !t.ceasedDate
  );
  const hasActiveCorporateTrustees = corporateTrustees.some(
    (t) => !t.ceasedDate
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="trustees-panel">
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xs font-semibold text-muted-foreground">INDIVIDUAL TRUSTEES</h3>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setIsAddIndividualDialogOpen(true)}
                    disabled={hasActiveCorporateTrustees}
                    data-testid="button-add-individual-trustee"
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Add Individual Trustee
                  </Button>
                </span>
              </TooltipTrigger>
              {hasActiveCorporateTrustees && (
                <TooltipContent>
                  <p>A trust cannot have individual trustees while a corporate trustee is appointed. Cease the corporate trustee first.</p>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        </div>

        {individualTrustees.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-6 text-center border rounded-md bg-white">
            <User className="h-8 w-8 text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">No individual trustees</p>
            <p className="text-xs text-muted-foreground mt-1">
              Add contacts as individual trustees
            </p>
          </div>
        ) : (
          <div className="border rounded-md bg-white">
            <Table>
              <TableHeader>
                <TableRow className="h-10 bg-muted/50">
                  <TableHead className="text-xs font-medium px-2 py-1">Contact Name</TableHead>
                  <TableHead className="text-xs font-medium px-2 py-1">Appointment Date</TableHead>
                  <TableHead className="text-xs font-medium px-2 py-1">Status</TableHead>
                  <TableHead className="text-xs font-medium px-2 py-1">Ceased Date</TableHead>
                  <TableHead className="text-xs font-medium px-2 py-1 w-[80px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {individualTrustees.map((trustee) => (
                  <TableRow
                    key={trustee.id}
                    className="h-10"
                    data-testid={`row-individual-trustee-${trustee.id}`}
                  >
                    <TableCell className="px-2 py-1">
                      <button
                        onClick={() => {
                          const url = buildNavigationUrl(`/contacts/${trustee.contactId}`, {
                            from: 'account',
                            fromId: account.id,
                            fromLabel: account.legalName || 'Account',
                          });
                          navigate(url);
                        }}
                        className="text-primary hover:underline text-sm"
                        data-testid={`link-trustee-contact-${trustee.id}`}
                      >
                        {getContactName(trustee)}
                      </button>
                    </TableCell>
                    <TableCell className="px-2 py-1 text-sm">
                      {formatDate(trustee.appointmentDate)}
                    </TableCell>
                    <TableCell className="px-2 py-1">
                      <Badge variant={trustee.ceasedDate ? "secondary" : "default"}>
                        {trustee.ceasedDate ? "Ceased" : "Active"}
                      </Badge>
                    </TableCell>
                    <TableCell className="px-2 py-1 text-sm">
                      {formatDate(trustee.ceasedDate)}
                    </TableCell>
                    <TableCell className="px-2 py-1">
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenEditIndividual(trustee)}
                          data-testid={`button-edit-individual-trustee-${trustee.id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setDeleteIndividualId(trustee.id)}
                          data-testid={`button-delete-individual-trustee-${trustee.id}`}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xs font-semibold text-muted-foreground">CORPORATE TRUSTEES</h3>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setIsAddCorporateDialogOpen(true)}
                    disabled={hasActiveIndividualTrustees}
                    data-testid="button-add-corporate-trustee"
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Add Corporate Trustee
                  </Button>
                </span>
              </TooltipTrigger>
              {hasActiveIndividualTrustees && (
                <TooltipContent>
                  <p>A trust cannot have a corporate trustee while individual trustees are appointed. Cease individual trustees first.</p>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        </div>

        {corporateTrustees.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-6 text-center border rounded-md bg-white">
            <Building2 className="h-8 w-8 text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">No corporate trustees</p>
            <p className="text-xs text-muted-foreground mt-1">
              Add company accounts as corporate trustees
            </p>
          </div>
        ) : (
          <div className="border rounded-md bg-white">
            <Table>
              <TableHeader>
                <TableRow className="h-10 bg-muted/50">
                  <TableHead className="text-xs font-medium px-2 py-1">Company Name</TableHead>
                  <TableHead className="text-xs font-medium px-2 py-1">ACN</TableHead>
                  <TableHead className="text-xs font-medium px-2 py-1">Appointment Date</TableHead>
                  <TableHead className="text-xs font-medium px-2 py-1">Status</TableHead>
                  <TableHead className="text-xs font-medium px-2 py-1">Ceased Date</TableHead>
                  <TableHead className="text-xs font-medium px-2 py-1 w-[80px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {corporateTrustees.map((trustee) => (
                  <TableRow
                    key={trustee.id}
                    className="h-10"
                    data-testid={`row-corporate-trustee-${trustee.id}`}
                  >
                    <TableCell className="px-2 py-1">
                      <button
                        onClick={() => {
                          const url = buildNavigationUrl(`/accounts/${trustee.trusteeAccountId}`, {
                            from: 'account',
                            fromId: account.id,
                            fromLabel: account.legalName || 'Account',
                          });
                          navigate(url);
                        }}
                        className="text-primary hover:underline text-sm"
                        data-testid={`link-trustee-account-${trustee.id}`}
                      >
                        {getCompanyName(trustee)}
                      </button>
                    </TableCell>
                    <TableCell className="px-2 py-1 text-sm text-muted-foreground">
                      {getCompanyAcn(trustee) || "-"}
                    </TableCell>
                    <TableCell className="px-2 py-1 text-sm">
                      {formatDate(trustee.appointmentDate)}
                    </TableCell>
                    <TableCell className="px-2 py-1">
                      <Badge variant={trustee.ceasedDate ? "secondary" : "default"}>
                        {trustee.ceasedDate ? "Ceased" : "Active"}
                      </Badge>
                    </TableCell>
                    <TableCell className="px-2 py-1 text-sm">
                      {formatDate(trustee.ceasedDate)}
                    </TableCell>
                    <TableCell className="px-2 py-1">
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenEditCorporate(trustee)}
                          data-testid={`button-edit-corporate-trustee-${trustee.id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setDeleteCorporateId(trustee.id)}
                          data-testid={`button-delete-corporate-trustee-${trustee.id}`}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <Dialog open={isAddIndividualDialogOpen} onOpenChange={setIsAddIndividualDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Individual Trustee</DialogTitle>
            <DialogDescription>
              Select a contact from the client group to add as an individual trustee.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Contact</Label>
              <ContactSelect
                value={selectedContactId}
                onValueChange={setSelectedContactId}
                clientGroupId={account.clientGroupId}
                placeholder="Select a contact"
                data-testid="select-individual-trustee-contact"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowCreateContactDialog(true)}
                className="mt-2 w-full"
              >
                <Plus className="mr-2 h-4 w-4" />
                Create New Contact
              </Button>
            </div>
            <div className="space-y-2">
              <Label>Appointment Date</Label>
              <StructureDatePicker
                value={appointmentDate}
                onChange={setAppointmentDate}
                testId="input-individual-trustee-appointment-date"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseIndividualDialog}>
              Cancel
            </Button>
            <Button
              onClick={handleAddIndividual}
              disabled={!selectedContactId || addIndividualMutation.isPending}
              data-testid="button-confirm-add-individual-trustee"
            >
              {addIndividualMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Add Trustee
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isAddCorporateDialogOpen} onOpenChange={setIsAddCorporateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Corporate Trustee</DialogTitle>
            <DialogDescription>
              Select a company account to add as a corporate trustee.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Company</Label>
              <Select value={selectedAccountId || undefined} onValueChange={setSelectedAccountId}>
                <SelectTrigger data-testid="select-corporate-trustee-account">
                  <SelectValue placeholder="Select a company" />
                </SelectTrigger>
                <SelectContent>
                  {companyAccounts
                    .filter(acc => acc.id !== account.id)
                    .map((acc) => (
                      <SelectItem key={acc.id} value={acc.id}>
                        {acc.legalName}
                        {acc.acn && ` (ACN: ${acc.acn})`}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowCreateCompanyDialog(true)}
                className="mt-2 w-full"
              >
                <Plus className="mr-2 h-4 w-4" />
                Create New Company
              </Button>
            </div>
            <div className="space-y-2">
              <Label>Appointment Date</Label>
              <StructureDatePicker
                value={appointmentDate}
                onChange={setAppointmentDate}
                testId="input-corporate-trustee-appointment-date"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseCorporateDialog}>
              Cancel
            </Button>
            <Button
              onClick={handleAddCorporate}
              disabled={!selectedAccountId || addCorporateMutation.isPending}
              data-testid="button-confirm-add-corporate-trustee"
            >
              {addCorporateMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Add Trustee
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteIndividualId} onOpenChange={(open) => !open && setDeleteIndividualId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Individual Trustee</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove this individual trustee? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteIndividualId(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteIndividualId && deleteIndividualMutation.mutate(deleteIndividualId)}
              disabled={deleteIndividualMutation.isPending}
              data-testid="button-confirm-delete-individual-trustee"
            >
              {deleteIndividualMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteCorporateId} onOpenChange={(open) => !open && setDeleteCorporateId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Corporate Trustee</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove this corporate trustee? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteCorporateId(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteCorporateId && deleteCorporateMutation.mutate(deleteCorporateId)}
              disabled={deleteCorporateMutation.isPending}
              data-testid="button-confirm-delete-corporate-trustee"
            >
              {deleteCorporateMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Individual Trustee Dialog */}
      <Dialog open={isEditIndividualDialogOpen} onOpenChange={setIsEditIndividualDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Individual Trustee</DialogTitle>
            <DialogDescription>
              Update trustee details for {editingIndividualTrustee && getContactName(editingIndividualTrustee)}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Appointment Date</Label>
              <StructureDatePicker
                value={editAppointmentDate}
                onChange={setEditAppointmentDate}
                placeholder="Select appointment date"
                testId="input-edit-individual-appointment-date"
              />
              {editAppointmentDate && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditAppointmentDate(undefined)}
                  className="w-full"
                >
                  Clear Appointment Date
                </Button>
              )}
            </div>
            <div className="space-y-2">
              <Label>Ceased Date</Label>
              <StructureDatePicker
                value={editCeasedDate}
                onChange={setEditCeasedDate}
                placeholder="Select date (or leave empty for Active)"
                testId="input-edit-individual-ceased-date"
              />
              <p className="text-xs text-muted-foreground">
                Leave empty to mark trustee as Active. Select a date to mark as Ceased.
              </p>
              {editCeasedDate && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditCeasedDate(undefined)}
                  className="w-full"
                >
                  Clear Date (Mark as Active)
                </Button>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseEditIndividualDialog}>
              Cancel
            </Button>
            <Button
              onClick={handleEditIndividual}
              disabled={editIndividualMutation.isPending}
              data-testid="button-confirm-edit-individual-trustee"
            >
              {editIndividualMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Corporate Trustee Dialog */}
      <Dialog open={isEditCorporateDialogOpen} onOpenChange={setIsEditCorporateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Corporate Trustee</DialogTitle>
            <DialogDescription>
              Update trustee details for {editingCorporateTrustee && getCompanyName(editingCorporateTrustee)}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Appointment Date</Label>
              <StructureDatePicker
                value={editAppointmentDate}
                onChange={setEditAppointmentDate}
                placeholder="Select appointment date"
                testId="input-edit-corporate-appointment-date"
              />
              {editAppointmentDate && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditAppointmentDate(undefined)}
                  className="w-full"
                >
                  Clear Appointment Date
                </Button>
              )}
            </div>
            <div className="space-y-2">
              <Label>Ceased Date</Label>
              <StructureDatePicker
                value={editCeasedDate}
                onChange={setEditCeasedDate}
                placeholder="Select date (or leave empty for Active)"
                testId="input-edit-corporate-ceased-date"
              />
              <p className="text-xs text-muted-foreground">
                Leave empty to mark trustee as Active. Select a date to mark as Ceased.
              </p>
              {editCeasedDate && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditCeasedDate(undefined)}
                  className="w-full"
                >
                  Clear Date (Mark as Active)
                </Button>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseEditCorporateDialog}>
              Cancel
            </Button>
            <Button
              onClick={handleEditCorporate}
              disabled={editCorporateMutation.isPending}
              data-testid="button-confirm-edit-corporate-trustee"
            >
              {editCorporateMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contact Creation Modal (for individual trustees) */}
      <ContactFormModal
        open={showCreateContactDialog}
        onOpenChange={setShowCreateContactDialog}
        onContactCreated={(newContact: Contact) => {
          setSelectedContactId(newContact.id);
          setShowCreateContactDialog(false);
          queryClient.invalidateQueries({ queryKey: ["/api/crm/contacts"] });
          toast({ title: "Contact created", description: "You can now select the newly created contact." });
        }}
        defaultClientGroupId={account.clientGroupId}
      />

      {/* Account Creation Modal (for corporate trustees) */}
      <AccountFormModal
        open={showCreateCompanyDialog}
        onOpenChange={setShowCreateCompanyDialog}
        onAccountCreated={(newAccount: Account) => {
          setSelectedAccountId(newAccount.id);
          setShowCreateCompanyDialog(false);
          queryClient.invalidateQueries({ queryKey: ["/api/crm/accounts"] });
          toast({ title: "Company created", description: "You can now select the newly created company." });
        }}
        defaultEntityType="company"
        defaultClientGroupId={account.clientGroupId}
      />
    </div>
  );
}
